import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { FileTextIcon, PhoneIcon, CalendarIcon, DollarSignIcon, SettingsIcon, RocketIcon, TrendingUpIcon, TestTubeIcon, ScaleIcon } from 'lucide-react';
const trackingMethods = [{
  icon: FileTextIcon,
  title: 'Lead Form Submissions',
  description: 'Every consultation request is tracked'
}, {
  icon: PhoneIcon,
  title: 'Phone Calls',
  description: 'Calls from the ad are recorded and attributed'
}, {
  icon: CalendarIcon,
  title: 'Online Bookings',
  description: 'Every completed booking fires a conversion'
}, {
  icon: DollarSignIcon,
  title: 'Revenue Attribution',
  description: 'Optional: Track patient value back to Google'
}];
const timelineSteps = [{
  week: 'Week 1',
  title: 'Setup & Build',
  icon: SettingsIcon,
  tasks: ['Conversion tracking', 'Campaign build', 'Ad copy', 'Geo-targeting', 'Negative keywords'],
  color: '#1B2A4A'
}, {
  week: 'Week 2',
  title: 'Launch',
  icon: RocketIcon,
  tasks: ['Launch all campaigns', 'Daily monitoring', 'Begin search term review'],
  color: '#2A3F6A'
}, {
  week: 'Weeks 3–4',
  title: 'First Optimization',
  icon: TrendingUpIcon,
  tasks: ['Pause underperformers', 'Refine keywords', 'First report'],
  color: '#C9A96E'
}, {
  week: 'Month 2',
  title: 'A/B Testing',
  icon: TestTubeIcon,
  tasks: ['Test ad copy', 'Expand top keywords', 'Adjust budgets based on data'],
  color: '#8B7355'
}, {
  week: 'Month 3+',
  title: 'Scale',
  icon: ScaleIcon,
  tasks: ['Scale winners', 'Add remarketing', 'Continuous optimization'],
  color: '#A8B4C4'
}];
export function TrackingTimeline() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  return <section id="timeline" className="py-24 md:py-32 bg-[#F8F9FA]" ref={ref} data-id="element-252">
      <div className="max-w-6xl mx-auto px-6" data-id="element-253">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6
      }} className="text-center mb-16" data-id="element-254">
          <p className="text-[#C9A96E] text-sm font-semibold tracking-[0.2em] uppercase mb-3" data-id="element-255">
            Tracking & Timeline
          </p>
          <h2 className="text-3xl md:text-4xl font-light text-[#1B2A4A]" data-id="element-256">
            How We'll Track Results
          </h2>
          <p className="text-[#666] mt-4 max-w-2xl mx-auto" data-id="element-257">
            We set up full conversion tracking before any ads go live.
          </p>
        </motion.div>

        {/* Tracking Methods */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6,
        delay: 0.2
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-20" data-id="element-258">
          {trackingMethods.map((method, index) => <motion.div key={method.title} initial={{
          opacity: 0,
          y: 20
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.4,
          delay: 0.3 + index * 0.1
        }} className="bg-white rounded-xl p-5 border border-[#1B2A4A]/5 shadow-sm" data-id="element-259">
              <div className="w-10 h-10 rounded-lg bg-[#1B2A4A]/5 flex items-center justify-center mb-3" data-id="element-260">
                <method.icon className="w-5 h-5 text-[#C9A96E]" data-id="element-261" />
              </div>
              <h3 className="font-semibold text-[#1B2A4A] text-sm mb-1" data-id="element-262">
                {method.title}
              </h3>
              <p className="text-xs text-[#666]" data-id="element-263">{method.description}</p>
            </motion.div>)}
        </motion.div>

        {/* Timeline */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6,
        delay: 0.4
      }} className="mb-8" data-id="element-264">
          <h3 className="text-xl font-semibold text-[#1B2A4A] text-center mb-12" data-id="element-265">
            Implementation Timeline
          </h3>

          <div className="relative" data-id="element-266">
            {/* Timeline line */}
            <div className="absolute top-8 left-0 right-0 h-0.5 bg-[#1B2A4A]/10 hidden lg:block" data-id="element-267" />

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6" data-id="element-268">
              {timelineSteps.map((step, index) => <motion.div key={step.week} initial={{
              opacity: 0,
              y: 30
            }} animate={isInView ? {
              opacity: 1,
              y: 0
            } : {}} transition={{
              duration: 0.5,
              delay: 0.5 + index * 0.1
            }} className="relative" data-id="element-269">
                  {/* Timeline dot */}
                  <div className="hidden lg:flex absolute top-0 left-1/2 -translate-x-1/2 w-16 h-16 rounded-full bg-white border-4 items-center justify-center z-10" style={{
                borderColor: step.color
              }} data-id="element-270">
                    <step.icon className="w-6 h-6" style={{
                  color: step.color
                }} data-id="element-271" />
                  </div>

                  {/* Card */}
                  <div className="lg:pt-24 bg-white rounded-xl p-5 border border-[#1B2A4A]/5 shadow-sm" data-id="element-272">
                    <div className="lg:hidden w-10 h-10 rounded-lg flex items-center justify-center mb-3" style={{
                  backgroundColor: `${step.color}15`
                }} data-id="element-273">
                      <step.icon className="w-5 h-5" style={{
                    color: step.color
                  }} data-id="element-274" />
                    </div>
                    <p className="text-xs font-semibold uppercase tracking-wider mb-1" style={{
                  color: step.color
                }} data-id="element-275">
                      {step.week}
                    </p>
                    <h4 className="font-semibold text-[#1B2A4A] mb-3" data-id="element-276">
                      {step.title}
                    </h4>
                    <ul className="space-y-1.5" data-id="element-277">
                      {step.tasks.map(task => <li key={task} className="text-xs text-[#666] flex items-start gap-2" data-id="element-278">
                          <span className="w-1 h-1 rounded-full mt-1.5 flex-shrink-0" style={{
                      backgroundColor: step.color
                    }} data-id="element-279" />
                          {task}
                        </li>)}
                    </ul>
                  </div>
                </motion.div>)}
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}